import React, { useState, useCallback, useContext, useEffect, useRef } from "react";
import { toast } from "react-toastify";
import { format, parseISO, isValid, formatDistanceToNowStrict } from "date-fns";
import ptBR from "date-fns/locale/pt-BR";

import { makeStyles, useTheme, alpha } from "@material-ui/core/styles";
import {
	Button,
	IconButton,
	Menu,
	MenuItem,
	Typography,
	CircularProgress,
} from "@material-ui/core";
import {
	WhatsApp,
	CropFree,
	MoreVert,
	Add,
	ReportProblemOutlined,
	ErrorOutline,
} from "@material-ui/icons";

import MainContainer from "../../components/MainContainer";
import api from "../../services/api";
import WhatsAppModal from "../../components/WhatsAppModal";
import QrcodeModal from "../../components/QrcodeModal";
import DeleteConnectionModal from "./DeleteConnectionModal";
import ConfirmationModal from "../../components/ConfirmationModal";
import { WhatsAppsContext } from "../../context/WhatsApp/WhatsAppsContext";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import { Can, canPerform } from "../../components/Can";
import EmptyState from "../../components/EmptyState";
import sidebarScrollbar from "../../helpers/sidebarScrollbar";
import formatConnectionNumber from "../../helpers/formatConnectionNumber";

// Tempo em OPENING após o qual a tela deixa de mostrar só "Aguarde" e
// passa a oferecer as ações de recuperação.
const OPENING_STUCK_MS = 45000;

// Mesmo limite de MAX_QRCODE_RETRIES no backend (libs/wbot.ts).
export const MAX_QR_CODES = 3;

const parseDate = value => {
	if (!value) return null;
	const date = typeof value === "string" ? parseISO(value) : new Date(value);
	return isValid(date) ? date : null;
};

const sinceText = value => {
	const date = parseDate(value);
	return date ? formatDistanceToNowStrict(date, { locale: ptBR }) : null;
};

// Cores por tom. No tema escuro usamos tons mais claros para manter contraste.
const toneColors = isDark => ({
	ok: isDark ? "#66bb6a" : "#2e7d32",
	warn: isDark ? "#ffb74d" : "#8a5200",
	warnDot: isDark ? "#ffb74d" : "#b26a00",
	error: isDark ? "#ef5350" : "#c62828",
	wait: isDark ? "#bdbdbd" : "#545454",
	waitDot: "#9e9e9e",
});

const useStyles = makeStyles(theme => {
	const isDark = theme.palette.type === "dark";
	const tones = toneColors(isDark);
	const border = theme.palette.divider;

	return {
		"@keyframes spin": {
			"0%": { transform: "rotate(0deg)" },
			"100%": { transform: "rotate(360deg)" },
		},
		scroll: {
			color: theme.palette.text.primary,
			flex: 1,
			overflowY: "auto",
			padding: theme.spacing(0, 0.75, 3),
			...sidebarScrollbar(theme),
		},
		header: {
			display: "flex",
			alignItems: "flex-end",
			justifyContent: "space-between",
			gap: theme.spacing(2),
			flexWrap: "wrap",
			padding: theme.spacing(0, 0.75, 2),
			color: theme.palette.text.primary,
		},
		title: {
			fontSize: 24,
			fontWeight: 600,
			lineHeight: 1.25,
			[theme.breakpoints.down("xs")]: { fontSize: 22 },
		},
		titleCount: { fontSize: 15, fontWeight: 500, color: theme.palette.text.secondary },
		muted: { color: theme.palette.text.secondary, fontSize: 14, marginTop: 4 },
		primaryBtn: {
			textTransform: "none",
			fontWeight: 600,
			borderRadius: 10,
			height: 40,
			boxShadow: "none",
			[theme.breakpoints.down("xs")]: { height: 44 },
		},
		stack: { display: "flex", flexDirection: "column", gap: theme.spacing(1.5) },
		alert: {
			display: "flex",
			flexWrap: "wrap",
			alignItems: "center",
			gap: theme.spacing(1.75),
			padding: theme.spacing(1.75, 2.25),
			borderRadius: 14,
			backgroundColor: alpha(tones.error, isDark ? 0.16 : 0.08),
			border: `1px solid ${alpha(tones.error, 0.3)}`,
		},
		alertIcon: {
			width: 36,
			height: 36,
			borderRadius: 10,
			display: "inline-flex",
			alignItems: "center",
			justifyContent: "center",
			flexShrink: 0,
			backgroundColor: theme.palette.background.paper,
			color: tones.error,
		},
		alertText: { flex: "1 1 320px", minWidth: 0 },
		alertTitle: { fontWeight: 600, fontSize: 14, color: tones.error },
		alertDesc: { fontSize: 13, color: theme.palette.text.secondary, marginTop: 2 },
		alertBtn: {
			textTransform: "none",
			fontWeight: 600,
			borderRadius: 10,
			boxShadow: "none",
			backgroundColor: tones.error,
			color: "#fff",
			"&:hover": { backgroundColor: alpha(tones.error, 0.85), boxShadow: "none" },
			[theme.breakpoints.down("xs")]: { width: "100%", height: 44 },
		},
		summary: {
			display: "grid",
			gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
			backgroundColor: theme.palette.background.paper,
			border: `1px solid ${border}`,
			borderRadius: 14,
			[theme.breakpoints.down("xs")]: { gridTemplateColumns: "minmax(0, 1fr)" },
		},
		summaryCell: {
			padding: theme.spacing(1.75, 2.25),
			"& + &": {
				borderLeft: `1px solid ${border}`,
				[theme.breakpoints.down("xs")]: { borderLeft: 0, borderTop: `1px solid ${border}` },
			},
		},
		summaryLabel: {
			display: "flex",
			alignItems: "center",
			gap: 6,
			fontSize: 11,
			fontWeight: 600,
			letterSpacing: "0.05em",
			textTransform: "uppercase",
			color: theme.palette.text.secondary,
		},
		summaryValue: { fontSize: 20, fontWeight: 700, marginTop: 4 },
		summarySub: { fontSize: 12, color: theme.palette.text.secondary },
		dot: { width: 8, height: 8, borderRadius: "50%", flexShrink: 0 },
		card: {
			display: "flex",
			flexWrap: "wrap",
			alignItems: "center",
			gap: theme.spacing(2, 2.5),
			padding: theme.spacing(2.25, 2.5),
			backgroundColor: theme.palette.background.paper,
			border: `1px solid ${border}`,
			borderRadius: 14,
			[theme.breakpoints.down("xs")]: { padding: theme.spacing(1.75) },
		},
		cardError: { borderColor: alpha(tones.error, 0.35) },
		identity: {
			display: "flex",
			alignItems: "center",
			gap: theme.spacing(1.75),
			flex: "1 1 280px",
			minWidth: 0,
		},
		avatar: {
			position: "relative",
			width: 44,
			height: 44,
			borderRadius: 12,
			display: "inline-flex",
			alignItems: "center",
			justifyContent: "center",
			flexShrink: 0,
		},
		avatarDot: {
			position: "absolute",
			right: -3,
			bottom: -3,
			width: 12,
			height: 12,
			borderRadius: "50%",
			border: `2px solid ${theme.palette.background.paper}`,
		},
		nameRow: { display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" },
		name: { fontSize: 16, fontWeight: 600, wordBreak: "break-word" },
		defaultBadge: {
			height: 20,
			display: "inline-flex",
			alignItems: "center",
			padding: "0 8px",
			borderRadius: 999,
			fontSize: 11,
			fontWeight: 600,
			backgroundColor: alpha(theme.palette.primary.main, 0.12),
			color: theme.palette.primary.main,
		},
		number: { fontSize: 13, color: theme.palette.text.secondary, marginTop: 2 },
		statusCol: { flex: "1 1 200px", minWidth: 0 },
		pill: {
			display: "inline-flex",
			alignItems: "center",
			gap: 6,
			height: 22,
			padding: "0 10px",
			borderRadius: 999,
			fontSize: 12,
			fontWeight: 600,
		},
		pillDot: { width: 6, height: 6, borderRadius: "50%" },
		pillSpinner: {
			width: 10,
			height: 10,
			borderRadius: "50%",
			border: "2px solid currentColor",
			borderTopColor: "transparent",
			boxSizing: "border-box",
			animation: "$spin 0.9s linear infinite",
		},
		statusSub: { fontSize: 12, color: theme.palette.text.secondary, marginTop: 4 },
		routeCol: {
			flex: "1 1 220px",
			minWidth: 0,
			display: "flex",
			flexWrap: "wrap",
			gap: 6,
			alignItems: "center",
		},
		routeLabel: { fontSize: 12, color: theme.palette.text.secondary, width: "100%" },
		queueChip: {
			display: "inline-flex",
			alignItems: "center",
			gap: 6,
			height: 24,
			padding: "0 10px",
			borderRadius: 999,
			fontSize: 12,
			fontWeight: 500,
			border: `1px solid ${border}`,
		},
		noRoute: {
			display: "inline-flex",
			alignItems: "center",
			gap: 6,
			fontSize: 12,
			fontWeight: 600,
			color: tones.warn,
		},
		linkBtn: {
			padding: 0,
			minWidth: 0,
			textTransform: "none",
			fontWeight: 600,
			fontSize: 12,
			verticalAlign: "baseline",
		},
		actions: {
			display: "flex",
			gap: 6,
			alignItems: "center",
			marginLeft: "auto",
			flexWrap: "wrap",
			justifyContent: "flex-end",
		},
		actionBtn: {
			textTransform: "none",
			fontWeight: 600,
			fontSize: 13,
			borderRadius: 8,
			height: 34,
			boxShadow: "none",
			whiteSpace: "nowrap",
			"&:hover": { boxShadow: "none" },
			[theme.breakpoints.down("xs")]: { height: 44 },
		},
		outlinedBtn: { borderColor: border, color: theme.palette.text.primary },
		dangerBtn: {
			borderColor: alpha(tones.error, 0.4),
			color: tones.error,
			"&:hover": { borderColor: tones.error, backgroundColor: alpha(tones.error, 0.06) },
		},
		menuDanger: { color: tones.error },
		footnote: { fontSize: 12, color: theme.palette.text.secondary, padding: theme.spacing(0.5, 0.25) },
		loadingWrapper: {
			display: "flex",
			justifyContent: "center",
			alignItems: "center",
			minHeight: 200,
		},
	};
});

// Texto, tom e ação de cada status. `stuck` = OPENING há mais de 45 s.
const getStatusInfo = (whatsApp, stuck) => {
	const { status, updatedAt, retries } = whatsApp;
	const since = sinceText(updatedAt);
	const date = parseDate(updatedAt);

	switch (status) {
		case "CONNECTED":
			return {
				tone: "ok",
				label: "Conectada",
				sub: date ? `Desde ${format(date, "dd/MM, HH:mm")}` : null,
			};
		case "DISCONNECTED":
			return {
				tone: "error",
				label: "Desconectada",
				sub: since ? `Caiu há ${since}` : "Mensagens deste número não estão chegando",
			};
		case "TIMEOUT":
			return {
				tone: "error",
				label: "Sem resposta do celular",
				sub: since ? `Há ${since}` : null,
			};
		case "qrcode":
			return {
				tone: "warn",
				label: "Aguardando leitura do QR",
				sub: retries ? `Código ${Math.min(retries, MAX_QR_CODES)} de ${MAX_QR_CODES}` : "Falta ler o código com o celular",
			};
		case "PENDING":
			return {
				tone: "warn",
				spinner: true,
				label: "Reconectando",
				sub: "A conexão oscilou e o sistema está tentando voltar sozinho",
			};
		case "PAIRING":
			return { tone: "warn", spinner: true, label: "Sincronizando", sub: "Carregando as conversas do celular" };
		case "OPENING":
			return stuck
				? {
						tone: "warn",
						label: "Demorando para conectar",
						sub: "Tente de novo ou gere um QR code novo",
				  }
				: {
						tone: "wait",
						spinner: true,
						label: "Conectando",
						sub: "Abrindo a sessão com o WhatsApp…",
				  };
		default:
			return { tone: "wait", label: status || "—", sub: null };
	}
};

const Connections = () => {
	const classes = useStyles();
	const theme = useTheme();
	const tones = toneColors(theme.palette.type === "dark");

	const { user } = useContext(AuthContext);
	const { whatsApps, loading } = useContext(WhatsAppsContext);
	const [whatsAppModalOpen, setWhatsAppModalOpen] = useState(false);
	const [qrModalOpen, setQrModalOpen] = useState(false);
	const [selectedWhatsApp, setSelectedWhatsApp] = useState(null);
	const [disconnectTarget, setDisconnectTarget] = useState(null);
	const [deleteTarget, setDeleteTarget] = useState(null);
	const [menu, setMenu] = useState({ anchor: null, whatsApp: null });

	// Válvula de escape para o estado OPENING: se a conexão passar muito tempo
	// "Conectando" sem sair do lugar, liberamos os botões de ação em vez de
	// deixar o usuário preso sem nenhuma saída.
	const openingSinceRef = useRef({});
	const [, forceTick] = useState(0);

	useEffect(() => {
		const since = openingSinceRef.current;
		const now = Date.now();

		whatsApps.forEach(({ id, status }) => {
			if (status === "OPENING") {
				if (!since[id]) since[id] = now;
			} else {
				delete since[id];
			}
		});
	}, [whatsApps]);

	// Atualiza a cada 5 s: libera o OPENING travado e mantém "caiu há X" em dia.
	useEffect(() => {
		const interval = setInterval(() => forceTick(t => t + 1), 5000);
		return () => clearInterval(interval);
	}, []);

	const isOpeningStuck = whatsAppId => {
		const since = openingSinceRef.current[whatsAppId];
		return !!since && Date.now() - since > OPENING_STUCK_MS;
	};

	// Abre o modal do QR já no clique: ele mostra "Gerando QR code" e troca
	// para o código assim que chega pelo socket. Se a sessão salva ainda for
	// válida e reconectar sem QR, o modal fecha sozinho.
	const openQrModalFor = whatsAppId => {
		const whatsApp = whatsApps.find(w => w.id === whatsAppId);
		if (!whatsApp) return;
		setSelectedWhatsApp(whatsApp);
		setQrModalOpen(true);
	};

	const handleStartWhatsAppSession = async whatsAppId => {
		try {
			await api.post(`/whatsappsession/${whatsAppId}`);
			openQrModalFor(whatsAppId);
		} catch (err) {
			toastError(err);
		}
	};

	const handleRequestNewQrCode = async whatsAppId => {
		try {
			await api.put(`/whatsappsession/${whatsAppId}`);
			openQrModalFor(whatsAppId);
		} catch (err) {
			toastError(err);
		}
	};

	const handleDisconnect = async () => {
		if (!disconnectTarget) return;
		try {
			await api.delete(`/whatsappsession/${disconnectTarget.id}`);
		} catch (err) {
			toastError(err);
		}
		setDisconnectTarget(null);
	};

	const handleDelete = async () => {
		if (!deleteTarget) return;
		try {
			await api.delete(`/whatsapp/${deleteTarget.id}`);
			toast.success("Conexão excluída.");
		} catch (err) {
			toastError(err);
		}
		setDeleteTarget(null);
	};

	const handleSetDefault = async whatsApp => {
		try {
			await api.put(`/whatsapp/${whatsApp.id}`, { isDefault: true });
			toast.success(`${whatsApp.name} agora é a conexão padrão.`);
		} catch (err) {
			toastError(err);
		}
	};

	const handleOpenWhatsAppModal = () => {
		setSelectedWhatsApp(null);
		setWhatsAppModalOpen(true);
	};

	const handleCloseWhatsAppModal = useCallback(() => {
		setWhatsAppModalOpen(false);
		setSelectedWhatsApp(null);
	}, []);

	// Conexão recém-criada: abre o QR code direto.
	const handleWhatsAppCreated = useCallback(whatsApp => {
		setSelectedWhatsApp(whatsApp);
		setQrModalOpen(true);
	}, []);

	const handleCloseQrModal = useCallback(() => {
		setSelectedWhatsApp(null);
		setQrModalOpen(false);
	}, []);

	const handleEditWhatsApp = whatsApp => {
		setSelectedWhatsApp(whatsApp);
		setWhatsAppModalOpen(true);
	};

	const openMenu = (event, whatsApp) => setMenu({ anchor: event.currentTarget, whatsApp });
	const closeMenu = () => setMenu({ anchor: null, whatsApp: null });
	const runFromMenu = fn => () => {
		const target = menu.whatsApp;
		closeMenu();
		fn(target);
	};

	const pillStyle = tone => {
		const color = tones[tone];
		return { backgroundColor: alpha(tones[`${tone}Dot`] || color, 0.12), color };
	};
	const dotColor = tone => tones[`${tone}Dot`] || tones[tone];

	const renderAvatar = (whatsApp, info) => {
		const isQr = whatsApp.status === "qrcode";
		const isOk = info.tone === "ok";
		const bg = isOk
			? alpha("#25D366", 0.14)
			: isQr
			? alpha(tones.warnDot, 0.12)
			: theme.palette.action.hover;
		const color = isOk ? "#128c4a" : isQr ? tones.warn : theme.palette.text.secondary;

		return (
			<span className={classes.avatar} style={{ backgroundColor: bg, color }}>
				{isQr ? <CropFree /> : <WhatsApp />}
				<span className={classes.avatarDot} style={{ backgroundColor: dotColor(info.tone) }} />
			</span>
		);
	};

	const renderRoute = whatsApp => {
		const queues = whatsApp.queues || [];
		if (!queues.length && !whatsApp.promptId) {
			return (
				<div>
					<div className={classes.noRoute}>
						<ErrorOutline style={{ fontSize: 16 }} />
						Sem fila nem IA
					</div>
					<div className={classes.statusSub} style={{ marginTop: 2 }}>
						As conversas chegam sem setor.{" "}
						<Can
							role={user.profile}
							perform="connections-page:editOrDeleteConnection"
							yes={() => (
								<Button color="primary" className={classes.linkBtn} onClick={() => handleEditWhatsApp(whatsApp)}>
									Definir filas
								</Button>
							)}
						/>
					</div>
				</div>
			);
		}

		return (
			<>
				<span className={classes.routeLabel}>Encaminha para</span>
				{queues.map(queue => (
					<span key={queue.id} className={classes.queueChip}>
						<span className={classes.dot} style={{ backgroundColor: queue.color || "#9e9e9e" }} />
						{queue.name}
					</span>
				))}
				{whatsApp.promptId && !queues.length && (
					<span className={classes.queueChip}>Assistente de IA</span>
				)}
			</>
		);
	};

	// Só o botão que resolve o status atual; o resto fica no menu ⋮.
	const renderPrimaryActions = (whatsApp, stuck) => {
		const { status, id } = whatsApp;
		const btn = (label, onClick, variant = "contained") => (
			<Button
				key={label}
				className={`${classes.actionBtn} ${variant === "outlined" ? classes.outlinedBtn : ""}`}
				size="small"
				variant={variant}
				color={variant === "contained" ? "primary" : "default"}
				onClick={onClick}
			>
				{label}
			</Button>
		);

		if (status === "qrcode") return btn("Mostrar QR code", () => openQrModalFor(id));
		if (status === "DISCONNECTED" || status === "TIMEOUT") {
			return [
				btn("Reconectar", () => handleStartWhatsAppSession(id)),
				btn("Novo QR code", () => handleRequestNewQrCode(id), "outlined"),
			];
		}
		if (status === "PENDING") {
			return [
				btn("Tentar de novo", () => handleStartWhatsAppSession(id)),
				<Button
					key="disconnect"
					className={`${classes.actionBtn} ${classes.dangerBtn}`}
					size="small"
					variant="outlined"
					onClick={() => setDisconnectTarget(whatsApp)}
				>
					Desconectar
				</Button>,
			];
		}
		if (status === "OPENING") {
			if (stuck) {
				return [
					btn("Tentar de novo", () => handleStartWhatsAppSession(id)),
					btn("Novo QR code", () => handleRequestNewQrCode(id), "outlined"),
				];
			}
			return (
				<Button className={classes.actionBtn} size="small" variant="outlined" disabled>
					Aguarde…
				</Button>
			);
		}
		return null;
	};

	const canDisconnect = (whatsApp, stuck) =>
		["CONNECTED", "PAIRING", "PENDING", "qrcode"].includes(whatsApp.status) ||
		(whatsApp.status === "OPENING" && stuck);

	const renderCard = whatsApp => {
		const stuck = whatsApp.status === "OPENING" && isOpeningStuck(whatsApp.id);
		const info = getStatusInfo(whatsApp, stuck);
		const number = formatConnectionNumber(whatsApp.number);

		return (
			<div
				key={whatsApp.id}
				className={`${classes.card} ${info.tone === "error" ? classes.cardError : ""}`}
			>
				<div className={classes.identity}>
					{renderAvatar(whatsApp, info)}
					<div style={{ minWidth: 0 }}>
						<div className={classes.nameRow}>
							<span className={classes.name}>{whatsApp.name}</span>
							{whatsApp.isDefault && <span className={classes.defaultBadge}>Padrão</span>}
						</div>
						<div className={classes.number}>
							{number || "Número aparece após conectar"}
						</div>
					</div>
				</div>

				<div className={classes.statusCol}>
					<span className={classes.pill} style={pillStyle(info.tone)}>
						{info.spinner ? (
							<span className={classes.pillSpinner} />
						) : (
							<span className={classes.pillDot} style={{ backgroundColor: dotColor(info.tone) }} />
						)}
						{info.label}
					</span>
					{info.sub && <div className={classes.statusSub}>{info.sub}</div>}
				</div>

				<div className={classes.routeCol}>{renderRoute(whatsApp)}</div>

				<div className={classes.actions}>
					<Can
						role={user.profile}
						perform="connections-page:actionButtons"
						yes={() => renderPrimaryActions(whatsApp, stuck)}
					/>
					{whatsApp.status === "CONNECTED" && (
						<Can
							role={user.profile}
							perform="connections-page:editOrDeleteConnection"
							yes={() => (
								<Button
									className={`${classes.actionBtn} ${classes.outlinedBtn}`}
									size="small"
									variant="outlined"
									onClick={() => handleEditWhatsApp(whatsApp)}
								>
									Editar
								</Button>
							)}
						/>
					)}
					<IconButton
						size="small"
						aria-label={`Mais ações de ${whatsApp.name}`}
						onClick={event => openMenu(event, whatsApp)}
						style={{ width: 34, height: 34, borderRadius: 8 }}
					>
						<MoreVert fontSize="small" />
					</IconButton>
				</div>
			</div>
		);
	};

	const renderMenu = () => {
		const target = menu.whatsApp;
		if (!target) return null;
		const stuck = target.status === "OPENING" && isOpeningStuck(target.id);
		const items = [];
		const canEdit = canPerform(user.profile, "connections-page:editOrDeleteConnection");
		const canAct = canPerform(user.profile, "connections-page:actionButtons");

		if (canEdit) {
			items.push(
				<MenuItem key="edit" onClick={runFromMenu(handleEditWhatsApp)}>Editar</MenuItem>
			);
			if (!target.isDefault) {
				items.push(
					<MenuItem key="default" onClick={runFromMenu(handleSetDefault)}>Definir como padrão</MenuItem>
				);
			}
		}
		if (canAct && canDisconnect(target, stuck)) {
			items.push(
				<MenuItem key="disconnect" onClick={runFromMenu(setDisconnectTarget)}>
					Desconectar
				</MenuItem>
			);
		}
		if (canEdit) {
			items.push(
				<MenuItem key="delete" className={classes.menuDanger} onClick={runFromMenu(setDeleteTarget)}>
					Excluir conexão
				</MenuItem>
			);
		}
		if (!items.length) {
			items.push(
				<MenuItem key="none" disabled>Sem ações disponíveis</MenuItem>
			);
		}
		return items;
	};

	const list = whatsApps || [];
	const connected = list.filter(w => w.status === "CONNECTED");
	const disconnected = list.filter(w => ["DISCONNECTED", "TIMEOUT"].includes(w.status));
	const waitingQr = list.filter(w => w.status === "qrcode");
	const attention = list.filter(
		w => w.status !== "CONNECTED" && !(w.status === "OPENING" && !isOpeningStuck(w.id))
	);
	const withoutRoute = list.filter(w => !w.queues?.length && !w.promptId);

	const attentionParts = [];
	if (disconnected.length) attentionParts.push(`${disconnected.length} desconectada${disconnected.length > 1 ? "s" : ""}`);
	if (waitingQr.length) attentionParts.push(`${waitingQr.length} aguardando QR code`);
	const otherAttention = attention.length - disconnected.length - waitingQr.length;
	if (otherAttention > 0) attentionParts.push(`${otherAttention} reconectando`);

	return (
		<MainContainer>
			<ConfirmationModal
				title={disconnectTarget ? `Desconectar ${disconnectTarget.name}?` : ""}
				open={!!disconnectTarget}
				onClose={() => setDisconnectTarget(null)}
				onConfirm={handleDisconnect}
			>
				O número deixa de receber e enviar mensagens por aqui. As configurações
				ficam salvas e, para voltar, basta ler o QR code de novo.
			</ConfirmationModal>
			<DeleteConnectionModal
				whatsApp={deleteTarget}
				onClose={() => setDeleteTarget(null)}
				onConfirm={handleDelete}
				onDisconnectInstead={() => {
					const target = deleteTarget;
					setDeleteTarget(null);
					setDisconnectTarget(target);
				}}
			/>
			<QrcodeModal
				open={qrModalOpen}
				onClose={handleCloseQrModal}
				whatsAppId={!whatsAppModalOpen && selectedWhatsApp?.id}
			/>
			<WhatsAppModal
				open={whatsAppModalOpen}
				onClose={handleCloseWhatsAppModal}
				whatsAppId={!qrModalOpen && selectedWhatsApp?.id}
				onCreated={handleWhatsAppCreated}
			/>
			<Menu
				anchorEl={menu.anchor}
				open={Boolean(menu.anchor)}
				onClose={closeMenu}
				getContentAnchorEl={null}
				anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
				transformOrigin={{ vertical: "top", horizontal: "right" }}
			>
				{renderMenu()}
			</Menu>

			<div className={classes.header}>
				<div>
					<Typography component="h1" className={classes.title}>
						Conexões{" "}
						{list.length > 0 && <span className={classes.titleCount}>· {list.length}</span>}
					</Typography>
					<Typography className={classes.muted}>
						Números de WhatsApp ligados ao Message Express. Cada conexão recebe e envia as mensagens de um número.
					</Typography>
				</div>
				<Can
					role={user.profile}
					perform="connections-page:addConnection"
					yes={() => (
						<Button
							variant="contained"
							color="primary"
							startIcon={<Add />}
							className={classes.primaryBtn}
							onClick={handleOpenWhatsAppModal}
						>
							Nova conexão
						</Button>
					)}
				/>
			</div>

			{loading ? (
				<div className={classes.loadingWrapper}>
					<CircularProgress />
				</div>
			) : list.length === 0 ? (
				<EmptyState
					icon="📱"
					title="Nenhuma conexão WhatsApp ainda"
					description="Conecte seu WhatsApp para começar a receber e enviar mensagens. O processo leva menos de 2 minutos."
					actionLabel="+ Nova conexão"
					onAction={handleOpenWhatsAppModal}
				/>
			) : (
				<div className={classes.scroll}>
					<div className={classes.stack}>
						{disconnected.map(whatsApp => {
							const since = sinceText(whatsApp.updatedAt);
							const number = formatConnectionNumber(whatsApp.number);
							return (
								<div key={`alert-${whatsApp.id}`} className={classes.alert} role="alert">
									<span className={classes.alertIcon}>
										<ReportProblemOutlined fontSize="small" />
									</span>
									<div className={classes.alertText}>
										<div className={classes.alertTitle}>
											A conexão {whatsApp.name} caiu{since ? ` há ${since}` : ""}
										</div>
										<div className={classes.alertDesc}>
											Mensagens enviadas para {number || "este número"} não estão chegando no sistema até reconectar.
										</div>
									</div>
									<Can
										role={user.profile}
										perform="connections-page:actionButtons"
										yes={() => (
											<Button
												variant="contained"
												className={classes.alertBtn}
												onClick={() => handleStartWhatsAppSession(whatsApp.id)}
											>
												Reconectar agora
											</Button>
										)}
									/>
								</div>
							);
						})}

						<div className={classes.summary}>
							<div className={classes.summaryCell}>
								<div className={classes.summaryLabel}>
									<span className={classes.dot} style={{ backgroundColor: tones.ok }} />
									Conectadas
								</div>
								<div className={classes.summaryValue}>
									{connected.length}{" "}
									<span className={classes.titleCount}>de {list.length}</span>
								</div>
								<div className={classes.summarySub}>
									{connected.length ? connected.map(w => w.name).join(", ") : "Nenhuma no momento"}
								</div>
							</div>
							<div className={classes.summaryCell}>
								<div className={classes.summaryLabel}>
									<span className={classes.dot} style={{ backgroundColor: tones.error }} />
									Precisam de atenção
								</div>
								<div className={classes.summaryValue}>{attention.length}</div>
								<div className={classes.summarySub}>
									{attentionParts.length ? attentionParts.join(" · ") : "Tudo certo"}
								</div>
							</div>
							<div className={classes.summaryCell}>
								<div className={classes.summaryLabel}>
									<span className={classes.dot} style={{ backgroundColor: tones.warnDot }} />
									Sem fila nem IA
								</div>
								<div className={classes.summaryValue}>{withoutRoute.length}</div>
								<div className={classes.summarySub}>
									{withoutRoute.length ? "Mensagens chegam sem setor definido" : "Todas encaminham para um setor"}
								</div>
							</div>
						</div>

						{list.map(renderCard)}

						<Typography className={classes.footnote}>
							O status atualiza sozinho em tempo real. Se uma conexão cair, o aviso aparece no topo desta tela e no menu lateral.
						</Typography>
					</div>
				</div>
			)}
		</MainContainer>
	);
};

export default Connections;
